import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { customersApi, jobsApi, estimatesApi, invoicesApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import NewCustomerModal from "./NewCustomerModal";
import { CustomerDetail } from "../pages/Customers";
import { DocDetail } from "../pages/Estimates";

/**
 * Reusable customer profile overlay. Given a `customerId`, fetches the customer
 * plus the company's jobs/estimates/invoices and renders the same CustomerDetail
 * panel used on the Customers page — as an overlay on top of the current window.
 *
 * Estimate/invoice rows open the document in a nested DocDetail drawer; clicking
 * a job navigates to the Jobs page.
 */
export default function CustomerProfileOverlay({ customerId, onClose }) {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doc, setDoc] = useState(null); // { kind: "estimate"|"invoice", doc }
  const [editing, setEditing] = useState(false);
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "dispatcher";

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    setLoading(true);
    setCustomer(null);
    setDoc(null);
    (async () => {
      try {
        const [c, cs, js, es, inv] = await Promise.all([
          customersApi.get(customerId),
          customersApi.list().catch(() => []),
          jobsApi.list().catch(() => []),
          estimatesApi.list().catch(() => []),
          invoicesApi.list().catch(() => []),
        ]);
        if (!active) return;
        setCustomer(c);
        setCustomers(cs.length ? cs : [c]);
        setJobs(js);
        setEstimates(es);
        setInvoices(inv);
      } catch (e) {
        if (active) {
          toast.error("Couldn't load customer profile");
          onClose && onClose();
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const refreshDocs = async () => {
    const [es, inv] = await Promise.all([
      estimatesApi.list().catch(() => estimates),
      invoicesApi.list().catch(() => invoices),
    ]);
    setEstimates(es);
    setInvoices(inv);
  };

  if (!customerId) return null;

  if (loading || !customer) {
    return (
      <div className="fixed inset-0 z-[120] flex justify-end" data-testid="customer-profile-overlay">
        <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
        <div className="relative w-[640px] max-w-[95vw] h-full bg-white shadow-2xl border-l border-slate-200 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const allTags = [...new Set((customers || []).flatMap((c) => c.tags || []))];

  return (
    <div data-testid="customer-profile-overlay" className="relative z-[120]">
      <CustomerDetail
        customer={customer}
        canEdit={canEdit}
        onEdit={() => setEditing(true)}
        jobs={jobs}
        estimates={estimates}
        invoices={invoices}
        onClose={onClose}
        onUpdate={(u) => setCustomer(u)}
        onOpenEstimate={(est) => setDoc({ kind: "estimate", doc: est })}
        onOpenInvoice={(inv) => setDoc({ kind: "invoice", doc: inv })}
        onOpenJob={(jobId) => {
          onClose && onClose();
          navigate(`/jobs?id=${encodeURIComponent(jobId)}`);
        }}
      />
      <NewCustomerModal
        open={editing}
        editCustomer={customer}
        onClose={() => setEditing(false)}
        existingCustomers={customers}
        existingTags={allTags}
        onSaved={(saved) => {
          setCustomer(saved);
          setCustomers((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
          setEditing(false);
        }}
      />
      {doc && (
        <DocDetail
          kind={doc.kind}
          doc={doc.doc}
          customers={customers}
          jobs={jobs}
          onClose={() => setDoc(null)}
          onUpdated={(u) => {
            setDoc({ kind: doc.kind, doc: u });
            refreshDocs();
          }}
          onDeleted={() => {
            setDoc(null);
            refreshDocs();
          }}
          onConverted={() => {
            setDoc(null);
            refreshDocs();
          }}
        />
      )}
    </div>
  );
}
